import { DataLoader } from '@aiz-gl/omni-ui';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { catchError, firstValueFrom, map, of, tap } from 'rxjs';
import {
  ApplicationDCC,
  ApplicationDCCResponse,
  ClientsWithContractsResponse,
  UserList,
  UserPermissionsResponse,
  UsersListResponse,
  UserUpsertPayload,
} from '../../models/user.model';
import { ToastService } from '../toast/toast.service';

interface UserSearchFilter {
  name?: string;
  email?: string;
  [key: string]: unknown;
}

interface UserSearchRequest {
  pageNumber?: number;
  pageSize?: number;
  page?: number;
  page_size?: number;
  filter?: UserSearchFilter;
  [key: string]: unknown;
}

interface UserRoleServiceError {
  status_code?: number;
  status?: number;
  errors?: string[];
  message?: string;
}

@Injectable({
  providedIn: 'root',
})
export class UserRoleService {
  private readonly http = inject(HttpClient);
  private readonly toast = inject(ToastService);

  private readonly dccURL = 'v1/mdm/dcc';
  private readonly userListURL = 'v1/user/search';
  private readonly permissionsURL = 'v1/permission';
  private readonly deleteUserURL = 'v1/user/delete';
  private readonly addUpdateUserURL = 'v1/user/save';
  private readonly clientsWithContractsURL = 'v1/mdm/clients-with-contracts';

  private readonly _dccTimeZones = signal<Record<string, string>>({});

  getAllUserLists: DataLoader<UserList> = (request: unknown) => {
    const body = this.buildUserSearchPayload(request);
    return firstValueFrom(
      this.http.post<UsersListResponse>(this.userListURL, body).pipe(
        map((response) => ({
          data: response.data || [],
          totalRecords: response.pagination?.total_items || 0,
          pageNumber: response.pagination?.page || 1,
          pageSize: response.pagination?.page_size || 10,
        })),
        catchError((error: unknown) => {
          const serviceError = error as UserRoleServiceError;
          if (serviceError.status_code === 404 || serviceError.status === 404) {
            return of({
              data: [] as UserList[],
              totalRecords: 0,
              pageNumber: body.page || 1,
              pageSize: body.page_size || 10,
            });
          }
          const message =
            serviceError.errors?.[0] ||
            (typeof serviceError.message === 'string'
              ? serviceError.message
              : 'An unexpected error occurred. Please try again.');
          this.toast.error(message);
          throw error;
        }),
      ),
    );
  };

  private buildUserSearchPayload(request?: unknown) {
    const safeRequest = (request ?? {}) as UserSearchRequest;
    const { pageNumber, pageSize, filter, ...rest } = safeRequest;
    const normalizedFilter = this.normalizeUserSearchFilter(filter);

    return {
      ...rest,
      ...normalizedFilter,
      page: pageNumber ?? safeRequest.page ?? 1,
      page_size: pageSize ?? safeRequest.page_size ?? 10,
    };
  }

  private normalizeUserSearchFilter(filter?: UserSearchFilter) {
    const { name: rawName, email: rawEmail, ...rest } = filter ?? {};
    const name = typeof rawName === 'string' ? rawName.trim() : '';
    const email = typeof rawEmail === 'string' ? rawEmail.trim() : '';

    // When the single search box fills both name and email with the same value,
    // route it to the right field — but keep any other filters (role, dcc_code).
    if (name && email && name === email) {
      return this.looksLikeEmail(name)
        ? { ...rest, name: '', email: name }
        : { ...rest, name, email: '' };
    }

    return {
      ...rest,
      name,
      email,
    };
  }

  private looksLikeEmail(value: string) {
    return value.includes('@') || value.includes('.');
  }

  fetchAlldDccs() {
    return this.http.post<ApplicationDCCResponse>(this.dccURL, {}).pipe(
      tap((response) => this.cacheDccTimeZones(response?.data?.dcc ?? [])),
      map((response) => {
        return { data: response?.data || [] };
      }),
    );
  }

  private cacheDccTimeZones(dccs: ApplicationDCC[]): void {
    const map: Record<string, string> = {};
    for (const dcc of dccs) {
      if (dcc.dcc_code && dcc.time_zone) {
        map[dcc.dcc_code] = dcc.time_zone;
      }
    }
    this._dccTimeZones.set(map);
  }

  getTimeZoneFor(dccCode: string | null): string | null {
    if (!dccCode) return null;
    return this._dccTimeZones()[dccCode] ?? null;
  }

  fetchAllPermissions() {
    return this.http
      .post<UserPermissionsResponse>(this.permissionsURL, {})
      .pipe(
        map((response) => {
          return { data: response?.data?.functional_permissions || [] };
        }),
      );
  }

  fetchAllClientsWithContracts() {
    const body = { client_code: '', contract_code: '' };
    return this.http
      .post<ClientsWithContractsResponse>(this.clientsWithContractsURL, body)
      .pipe(map((response) => ({ data: response?.data || [] })));
  }

  addAndUpdateUser(userData: UserUpsertPayload) {
    return firstValueFrom(
      this.http.post(this.addUpdateUserURL, userData).pipe(
        catchError((error: unknown) => {
          throw error;
        }),
      ),
    );
  }

  deleteUser(emailId: string) {
    return firstValueFrom(
      this.http.post(this.deleteUserURL, { email: emailId }).pipe(
        catchError((error: unknown) => {
          throw error;
        }),
      ),
    );
  }
}
